import React, { useState, useEffect, useRef } from "react";
import styles from "./typewriter.module.scss";
import cx from "classnames";
import _ from "lodash";

interface TypewriterProps {
  textParts: Array<string | React.JSX.Element>;
  speed?: number; 
  className?: string;
  onComplete?: () => void;
  scrollToBottom?: () => void;
  seekAsset?: boolean;
}

const TypeWriter: React.FC<TypewriterProps> = ({
  textParts,
  speed = 5,
  className = "",
  onComplete,
  scrollToBottom,
  seekAsset
}) => {
  const [displayedParts, setDisplayedParts] = useState<
    Array<string | React.JSX.Element>
  >([]);
  const isTyping = useRef(false);

  useEffect(() => {
    setDisplayedParts([]);
    isTyping.current = false;
  }, [textParts]);

  useEffect(() => {
    if (isTyping.current) return;
    isTyping.current = true;

    let currentPartIndex = 0;
    let currentCharIndex = 0;
    let isUnmounted = false;

    const typeNext = () => {
      if (isUnmounted) return;

      if (currentPartIndex >= textParts.length) {
        if (onComplete) onComplete();
        return;
      }

      const currentPart = textParts[currentPartIndex];

      if (typeof currentPart === "string") {
        const nextChar = currentPart.charAt(currentCharIndex);
        setDisplayedParts((prev) => {
          const newParts = [...prev];
          if (
            newParts[currentPartIndex] &&
            typeof newParts[currentPartIndex] === "string"
          ) {
            newParts[currentPartIndex] =
              (newParts[currentPartIndex] as string) + nextChar;
          } else {
            newParts[currentPartIndex] = nextChar;
          }
          return newParts;
        });

        currentCharIndex++;

        if (currentCharIndex < currentPart.length) {
          setTimeout(typeNext, speed);
        } else {
          currentPartIndex++;
          currentCharIndex = 0;
          setTimeout(typeNext, speed);
        }
      } else {
        // It's a JSX element
        setDisplayedParts((prev) => [...prev, currentPart]);
        currentPartIndex++;
        setTimeout(typeNext, speed);
      }
    };

    typeNext();

    return () => {
      isUnmounted = true;
    };
  }, [textParts, speed]);

  useEffect(() => {
    if (scrollToBottom) {
      scrollToBottom();
    }
  }, [displayedParts]);

  const lastSentence = (_.last(displayedParts) as string) || "";
  useEffect(() => {
    const wordsCount = _.size(_.words(lastSentence));
    //scrolling when atleast 10 words are displayed
    if (wordsCount % 10 === 0 && scrollToBottom) {
      scrollToBottom();
    }
  }, [_.words(lastSentence)]);

  return (
    <div
      className={cx(
        styles.typewriterContainer,
        seekAsset && styles.assetAnswerLoading,
      )}
    >
      {displayedParts.map((part, index) => (
        <React.Fragment key={index}>{part}</React.Fragment>
      ))}
    </div>
  );
};

export default TypeWriter;
